import { DateTime } from 'luxon';
import { UserConsentId, UserConsentRecord, UserConsentRecords } from './user-consent-interface';

/**
 * The buyer's Advertising answer, and the rule that decides whether a web
 * purchase may be reported to Meta and Google Ads (#5587).
 *
 * The answer can be yes, no, or none. "None" is the ABSENCE of an answer — the
 * buyer never saw or never answered the question — so it has no enum value of its
 * own: everywhere below it is `undefined`.
 *
 * Where the answer comes from:
 * - the cookie dialog writes `_cf_consent_ad` (`'1'` / `'0'`), and the browser
 *   sends it with the checkout as the transport-only tracking key `ad_consent`;
 * - the Terms screen and the cookie preferences dialog save it on the account as
 *   the `advertising` consent record;
 * - the payment webhooks copy a checkout's answer onto that same record, but only
 *   when the checkout is newer than the record (see `recordCheckoutAdConsent`).
 *
 * The account record is the one the upload follows, because the trial-to-paid
 * upload happens about 14 days after the checkout, with no browser involved.
 */

export enum AdConsentAnswer {
  Yes = 'yes',
  No = 'no',
}

/** Read an answer from untrusted input. Anything but the two known words is
 *  "no answer" — never coerced into yes or no. */
export function parseAdConsentAnswer(value: unknown): AdConsentAnswer | undefined {
  if (value === AdConsentAnswer.Yes) return AdConsentAnswer.Yes;
  if (value === AdConsentAnswer.No) return AdConsentAnswer.No;
  return undefined;
}

/** The answer a `_cf_consent_ad` cookie value holds. A missing cookie, or any
 *  other value, is no answer. */
export function adConsentFromCookieValue(cookieValue: string | undefined): AdConsentAnswer | undefined {
  if (cookieValue === '1') return AdConsentAnswer.Yes;
  if (cookieValue === '0') return AdConsentAnswer.No;
  return undefined;
}

/** The cookie holding this device's Advertising answer: `'1'` or `'0'`. */
export const AD_CONSENT_COOKIE = '_cf_consent_ad';
/** The cookie holding WHEN that answer was given, as an ISO time (#5587). */
export const AD_CONSENT_AT_COOKIE = '_cf_consent_ad_at';

/** An answer and when it was given. `at` is absent when the time is unknown. */
export interface DatedAdConsent {
  answer: AdConsentAnswer;
  at?: string;
}

// the value itself when it is a readable ISO time, otherwise undefined
function validIso(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  return DateTime.fromISO(value, { zone: 'utc' }).isValid ? value : undefined;
}

/**
 * This device's Advertising answer, from its two cookies.
 *
 * An answer with no time next to it was written before #5587, and there the two
 * values do not mean the same. Until then, signing in wrote `'1'` for every
 * account holding a Marketing record — which was every account that accepted the
 * Terms, whatever it said — so an old `'1'` proves no yes and is ignored. Old
 * code wrote `'0'` only when a person switched Advertising off, so an old `'0'`
 * is a real no and is kept.
 */
export function readDeviceAdConsent(cookieValue: string | undefined, cookieAt: string | undefined): DatedAdConsent | undefined {
  const answer = adConsentFromCookieValue(cookieValue);
  if (!answer) return undefined;
  const at = validIso(cookieAt);
  if (at) return { answer, at };
  return answer === AdConsentAnswer.No ? { answer } : undefined;
}

/**
 * The cookie stamping WHEN this browser was first seen sending the Global
 * Privacy Control signal (#5663), as an ISO time.
 *
 * The signal itself is not stored — it is read fresh from the browser and from
 * the request header every time. What has to be remembered is the MOMENT it was
 * first seen, because an Advertising "yes" beats the signal only when the person
 * gave it afterwards.
 */
export const GPC_SINCE_COOKIE = '_cf_gpc_since';

/** The request header a browser with the signal switched on sends. */
export const GPC_HEADER = 'sec-gpc';

/**
 * Whether a `Sec-GPC` header value means the signal is on.
 *
 * The specification defines exactly one value that does, `1`, so nothing else
 * counts — not `true`, not `0`, not an empty header. Reading it loosely would
 * turn a header somebody set to `0`, which says the opposite, into an opt-out.
 */
export function gpcHeaderSaysOptOut(headerValue: string | undefined | null): boolean {
  return typeof headerValue === 'string' && headerValue.trim() === '1';
}

/**
 * The stamp to hold after a visit that either did or did not report the signal.
 *
 * It is written once and then never moves, and it is NEVER cleared by a visit
 * that reports no signal. Two reasons, and the second is the one that would bite:
 *
 * - An opt-out stays in force until the person opts back in. Nothing obliges us
 *   to notice that a browser stopped sending the signal, and treating silence as
 *   a fresh permission is the wrong direction to guess in.
 * - A browser that sets only the HTTP header, and not the page-visible property,
 *   looks signal-free to every page load and signalling only at the moment the
 *   server reads a request. If a negative reading cleared the stamp, such a
 *   browser would clear it on every load and re-stamp it at the next checkout —
 *   so an Advertising "yes" could never be dated later than it, and the person
 *   could never turn advertising back on at all.
 *
 * The way out is the one the ticket asks for: switching Advertising on in our own
 * dialog writes a "yes" dated now, which is later than any stamp already held.
 */
export function nextGpcSince(
  seenNow: boolean,
  storedSince: string | undefined,
  nowIso: string,
): string | undefined {
  const stored = validIso(storedSince);
  if (stored) return stored;
  if (!seenNow) return undefined;
  return validIso(nowIso);
}

/**
 * The Advertising answer to act on once the Global Privacy Control signal is
 * taken into account (#5663).
 *
 * With no stamp, the answer is whatever the person gave. With a stamp, the
 * answer is "no" unless they said yes AFTER it: the ticket calls a later choice
 * in our own dialog "more specific", and the California regulations say the same
 * — a consent given after the signal prevails, an earlier one does not.
 *
 * An answer carrying no time at all cannot be shown to be later, so it loses.
 * That is the safe direction and it costs nothing real: the cookie reader already
 * throws away an undated "yes", and an account record without a time is old.
 */
export function adConsentUnderGpc(
  known: DatedAdConsent | undefined,
  gpcSince: string | undefined,
): AdConsentAnswer | undefined {
  const since = validIso(gpcSince);
  if (!since) return known?.answer;
  if (known?.answer !== AdConsentAnswer.Yes) return AdConsentAnswer.No;
  const answeredAt = validIso(known.at);
  if (!answeredAt) return AdConsentAnswer.No;
  const isLater = DateTime.fromISO(answeredAt, { zone: 'utc' }).toMillis()
    > DateTime.fromISO(since, { zone: 'utc' }).toMillis();
  return isLater ? AdConsentAnswer.Yes : AdConsentAnswer.No;
}

/**
 * The same rule applied by a SERVER that has just seen `Sec-GPC: 1` on a
 * checkout request, to the answer the browser sent with it (#5663).
 *
 * The server holds no stamp of its own and cannot compare times, so it asks a
 * narrower question: had the browser seen a signal when it decided? That is what
 * `browserHadSeenGpc` says, and the browser reports it by sending its own stamp
 * along with the checkout.
 *
 * - The browser HAD seen one and still says yes: it already applied the rule
 *   above and found the yes to be the later choice, so the yes stands.
 * - The browser had NOT seen one: its yes was decided in ignorance of the very
 *   signal this server is reacting to, so it becomes a no. This is the case the
 *   whole function exists for — a browser that sets only the header cannot see
 *   its own signal, and trusting its yes would report exactly the person the
 *   ticket is about.
 * - Anything that is not a yes becomes a no either way.
 */
export function adConsentUnderRequestGpc(
  answer: AdConsentAnswer | undefined,
  requestSaysGpc: boolean,
  browserHadSeenGpc = false,
): AdConsentAnswer | undefined {
  if (!requestSaysGpc) return answer;
  const yesStands = answer === AdConsentAnswer.Yes && browserHadSeenGpc;
  return yesStands ? AdConsentAnswer.Yes : AdConsentAnswer.No;
}

/** The answer as Mixpanel sees it: the event property and the profile property
 *  `ad_consent`. Unlike the upload rule, Mixpanel needs a word for "no answer". */
export function adConsentTelemetryValue(answer: AdConsentAnswer | undefined): string {
  return answer ?? 'none';
}

/**
 * The Advertising record stored on an account, normalised.
 *
 * The document may have been written by an older app or be damaged, so the record
 * is checked rather than trusted: it must be an object whose `accepted` is a real
 * boolean. Anything else reads as no answer.
 */
export function readStoredAdConsent(consents: UserConsentRecords | undefined | null): AdConsentAnswer | undefined {
  const record: unknown = consents?.[UserConsentId.Advertising];
  if (typeof record !== 'object' || record === null) return undefined;
  const accepted = (record as { accepted?: unknown }).accepted;
  if (accepted === true) return AdConsentAnswer.Yes;
  if (accepted === false) return AdConsentAnswer.No;
  return undefined;
}

/** The account's Advertising record with its time, normalised like
 *  {@link readStoredAdConsent}. */
export function readStoredAdConsentRecord(consents: UserConsentRecords | undefined | null): DatedAdConsent | undefined {
  const answer = readStoredAdConsent(consents);
  if (!answer) return undefined;
  const at = validIso((consents?.[UserConsentId.Advertising] as { timestamp_utc?: unknown }).timestamp_utc);
  return at ? { answer, at } : { answer };
}

/** What signing in should do to line the device's answer up with the account's. */
export enum AdConsentSync {
  Nothing = 'nothing',
  /** the account's answer is newer, or the device has none: write it to the cookies */
  CopyAccountToDevice = 'copy-account-to-device',
  /** the device's answer is newer, or the account has none: save it on the account */
  SendDeviceToAccount = 'send-device-to-account',
}

/**
 * Line up the device's and the account's Advertising answers (#5587): the newer
 * answer wins.
 *
 * An answer with no time loses to one with a time, because it cannot be shown to
 * be newer. The one exception is an account with no answer at all: then any
 * device answer is sent, since it is still the only answer the person gave.
 */
export function decideAdConsentSync(device: DatedAdConsent | undefined, account: DatedAdConsent | undefined): AdConsentSync {
  if (!device && !account) return AdConsentSync.Nothing;
  if (!device) return AdConsentSync.CopyAccountToDevice;
  if (!account) return AdConsentSync.SendDeviceToAccount;
  const deviceMs = device.at ? DateTime.fromISO(device.at, { zone: 'utc' }).toMillis() : undefined;
  const accountMs = account.at ? DateTime.fromISO(account.at, { zone: 'utc' }).toMillis() : undefined;
  const deviceIsNewer = deviceMs !== undefined && (accountMs === undefined || deviceMs > accountMs);
  if (device.answer === account.answer) {
    // the same answer: only the device's missing or older time needs refreshing
    return deviceIsNewer || deviceMs === accountMs ? AdConsentSync.Nothing : AdConsentSync.CopyAccountToDevice;
  }
  return deviceIsNewer ? AdConsentSync.SendDeviceToAccount : AdConsentSync.CopyAccountToDevice;
}

/** The `appVersion` a webhook writes on the record, so a reader can tell a
 *  checkout's answer from one the user saved in the app. */
export enum AdConsentCheckoutSource {
  Paddle = 'checkout:paddle',
  PayPro = 'checkout:paypro',
}

/**
 * Copy a checkout's Advertising answer onto the account, unless the account
 * already holds an answer at least as new.
 *
 * The age check is not optional. Paddle keeps the checkout's data on the
 * subscription and repeats it on every later webhook for that subscription, so
 * without it an old checkout's "yes" would overwrite a newer "no" — one given in
 * the app, or at a later checkout — every time Paddle wrote about the old
 * subscription again.
 *
 * `checkoutAtUtc` must be a SERVER-trusted time for the checkout (Paddle's
 * subscription creation time, or the time a PayPro notification was received),
 * never the browser's clock, which can be hours off.
 *
 * A checkout with no answer changes nothing: the stored answer stays (operator
 * decision, 2026-09-17). Returns whether the record was written.
 */
export function recordCheckoutAdConsent(
  user: { consents?: UserConsentRecords },
  answer: AdConsentAnswer | undefined,
  checkoutAtUtc: string,
  source: AdConsentCheckoutSource,
): boolean {
  if (!answer) return false;
  const checkoutAt = DateTime.fromISO(checkoutAtUtc, { zone: 'utc' });
  // a checkout time we cannot read cannot be compared, so it cannot be shown to
  // be newer than anything — leave the account alone
  if (!checkoutAt.isValid) return false;

  // `consents` itself may be damaged on an old document: anything that is not a
  // plain object is replaced rather than written into
  const consents = user.consents;
  const usable = typeof consents === 'object' && consents !== null && !Array.isArray(consents);
  const existing: unknown = usable ? consents[UserConsentId.Advertising] : undefined;
  if (readStoredAdConsent(usable ? consents : undefined) !== undefined) {
    const storedAt = DateTime.fromISO(String((existing as UserConsentRecord).timestamp_utc), { zone: 'utc' });
    // a stored answer with an unreadable time is replaced: we cannot tell it is
    // newer, and the checkout's time is known
    if (storedAt.isValid && storedAt.toMillis() >= checkoutAt.toMillis()) return false;
  }

  const record: UserConsentRecord = {
    accepted: answer === AdConsentAnswer.Yes,
    timestamp_utc: checkoutAt.toISO(),
    appVersion: source,
  };
  user.consents = { ...(usable ? consents : {}), [UserConsentId.Advertising]: record };
  return true;
}

/**
 * The countries where an upload needs an explicit "yes": the EU, the rest of the
 * EEA (Iceland, Liechtenstein, Norway), the United Kingdom and Switzerland.
 * ISO 3166-1 alpha-2 codes, plus the two non-ISO codes some systems use for
 * members: `UK` for the United Kingdom and `EL` for Greece.
 */
export const OPT_IN_AD_CONSENT_COUNTRIES: ReadonlySet<string> = new Set([
  // EU
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // rest of the EEA
  'IS', 'LI', 'NO',
  // United Kingdom and Switzerland
  'GB', 'CH',
  // non-ISO aliases
  'UK', 'EL',
]);

/** Which rule applies to a buyer. */
export enum AdConsentRegion {
  /** EU/EEA, UK or Switzerland: upload only after a "yes". */
  OptIn = 'opt-in',
  /** Nothing says where the buyer is, so they are treated as `OptIn`. Kept
   *  separate only so the log line can say why. */
  Unknown = 'unknown',
  /** Everywhere else: upload unless the buyer said "no". */
  OptOut = 'opt-out',
}

/** Which consent rule applies to a buyer in this one country. */
export function adConsentRegionForCountry(country: string | undefined | null): AdConsentRegion {
  const code = typeof country === 'string' ? country.trim().toUpperCase() : '';
  if (!code) return AdConsentRegion.Unknown;
  return OPT_IN_AD_CONSENT_COUNTRIES.has(code) ? AdConsentRegion.OptIn : AdConsentRegion.OptOut;
}

/**
 * Everything we know about where a buyer is. Both are two-letter country codes
 * and both are optional, because either can be missing on a real purchase.
 */
export interface BuyerCountrySignals {
  /** The country on the card or billing address the buyer paid with. */
  billingCountry?: string | undefined | null;
  /** The country the buyer's own connection was in when they checked out
   *  (#5664). PayPro works this out itself and sends it on the notification;
   *  on the Paddle rail it is looked up from the checkout's IP address. */
  ipCountry?: string | undefined | null;
}

/**
 * Which consent rule applies to a buyer, from every signal we have about where
 * they are (#5664).
 *
 * Opt-in wins. A buyer is treated as being in the EU/EEA/UK/Switzerland when
 * EITHER signal places them there, because either one being right is enough to
 * make an upload without consent a breach. The two disagree often enough to
 * matter: someone living in the EU can pay with a card registered abroad, and
 * someone living abroad can pay with an EU card.
 *
 * Reading the billing country alone — which is what #5587 shipped — gets the
 * first of those wrong in the expensive direction. Reading the connection alone
 * would get the second wrong the same way, because a person at home in the EU
 * behind a VPN that exits in the United States looks American by connection and
 * European by card. So neither signal replaces the other; the union is the only
 * combination that cannot report a European without their consent.
 *
 * The price is measurement, not legality: a buyer genuinely outside the EU who
 * has either an EU card or an EU VPN exit is held back although we could have
 * reported them. The ticket ranks that as the cheap direction.
 *
 * "Unknown" is the absence of an opinion, never an opinion of its own: it
 * cannot pull a known country either way.
 */
export function adConsentRegionForBuyer(signals: BuyerCountrySignals): AdConsentRegion {
  const billing = adConsentRegionForCountry(signals.billingCountry);
  const ip = adConsentRegionForCountry(signals.ipCountry);
  if (billing === AdConsentRegion.OptIn || ip === AdConsentRegion.OptIn) return AdConsentRegion.OptIn;
  if (billing === AdConsentRegion.OptOut || ip === AdConsentRegion.OptOut) return AdConsentRegion.OptOut;
  return AdConsentRegion.Unknown;
}

/**
 * Whether working out the buyer's country from their checkout IP could still
 * change the upload decision (#5664).
 *
 * The Paddle rail has no country of its own and has to look one up, which costs
 * a call to an outside service. This says when that call is worth making, and it
 * is deliberately the SAME arithmetic as {@link adConsentRegionForBuyer} rather
 * than a hand-written summary of it, so the two cannot drift apart:
 *
 * - an answer of either kind decides the upload on its own, so the region is
 *   never consulted and no lookup can change anything;
 * - with no answer and a billing country already inside the EU/EEA/UK/CH, we are
 *   holding the upload back — and because opt-in wins, no IP country can move it
 *   the other way, so there is nothing to learn;
 * - that leaves two cases, and the lookup can change both. A billing country
 *   OUTSIDE the EU is today's upload-without-consent case and the one the ticket
 *   is about: an EU connection holds the upload back. NO billing country at all
 *   is the other way round: today nothing places the buyer, so we hold the
 *   upload back, and a connection from outside the EU lets it through.
 *
 * Skipping the lookup in the other cases is not an approximation: whether the
 * upload happens, and what Google is told about consent, are identical either
 * way — which is what the tests pin. What it costs is precision in the LOG
 * line. A skipped lookup can leave the logged region saying "opt-out" for
 * somebody a lookup would have placed in the EU, and can name the wrong one of
 * the two hold-it-back reasons ("nothing says where they are" instead of "they
 * are in the EU"). Nothing reads either field except the log, and the decision
 * beside them is right, so the trade is an outside call against a label.
 */
export function ipCountryCouldChangeAdUpload(
  answer: AdConsentAnswer | undefined,
  billingCountry: string | undefined | null,
): boolean {
  if (answer) return false;
  return adConsentRegionForCountry(billingCountry) !== AdConsentRegion.OptIn;
}

/** Google Ads' consent values, as its upload API spells them. */
export enum GoogleAdsConsentStatus {
  Granted = 'GRANTED',
  Unspecified = 'UNSPECIFIED',
}

/** Why an upload was not sent. The values appear in the log line. */
export enum AdUploadSkipReason {
  /** the buyer said no — anywhere */
  Declined = 'declined',
  /** no answer, and the buyer is in the EU/EEA, UK or Switzerland */
  NoAnswerOptInRegion = 'no-answer-opt-in-region',
  /** no answer, and nothing says where the buyer is */
  NoAnswerCountryUnknown = 'no-answer-country-unknown',
}

export interface AdUploadDecision {
  send: boolean;
  /** set exactly when `send` is false */
  skipReason?: AdUploadSkipReason;
  region: AdConsentRegion;
  /** what Google is told for both `adUserData` and `adPersonalization`.
   *  GRANTED only after a "yes" — never without one. */
  googleConsent: GoogleAdsConsentStatus;
}

/**
 * The upload rule from #5587, on the region #5664 works out:
 *
 * | where the buyer is       | yes  | no          | no answer   |
 * |--------------------------|------|-------------|-------------|
 * | EU/EEA, UK, Switzerland  | send | don't send  | don't send  |
 * | anywhere else            | send | don't send  | send        |
 * | unknown                  | as EU/EEA/UK/CH                      |
 *
 * Note that the region is read ONLY on the "no answer" row: an answer of either
 * kind decides the upload wherever the buyer is. It is still reported on every
 * decision, because the log line names it.
 *
 * Which countries put a buyer on which row is {@link adConsentRegionForBuyer}.
 */
export function decideAdUpload(answer: AdConsentAnswer | undefined, countries: BuyerCountrySignals): AdUploadDecision {
  const region = adConsentRegionForBuyer(countries);
  if (answer === AdConsentAnswer.Yes) {
    return { send: true, region, googleConsent: GoogleAdsConsentStatus.Granted };
  }
  if (answer === AdConsentAnswer.No) {
    return { send: false, skipReason: AdUploadSkipReason.Declined, region, googleConsent: GoogleAdsConsentStatus.Unspecified };
  }
  if (region === AdConsentRegion.OptOut) {
    return { send: true, region, googleConsent: GoogleAdsConsentStatus.Unspecified };
  }
  const skipReason = region === AdConsentRegion.Unknown
    ? AdUploadSkipReason.NoAnswerCountryUnknown
    : AdUploadSkipReason.NoAnswerOptInRegion;
  return { send: false, skipReason, region, googleConsent: GoogleAdsConsentStatus.Unspecified };
}
